import { useEffect, useState, Fragment } from "react";
import { useNavigate } from "react-router-dom";
import { LogOut } from "lucide-react";
import { api } from "../../lib/api";
import { LOGO_PINK } from "../../lib/data";
import {
    BookingsTab, OrdersTab, ProductsTab, EventPackagesTab, FlavorsTab, CategoriesTab, PacksTab,
    FaqsTab, PagesTab, WholesaleTab, SignupsTab, ContactsTab, SettingsTab, RetailersTab, ProductInterestTab,
} from "./tabs";

const TABS = [
    ["bookings", "Booking Requests", BookingsTab, "bookings"],
    ["orders", "Orders", OrdersTab, "orders"],
    ["messages", "Messages", ContactsTab, "messages"],
    ["wholesale", "Wholesale", WholesaleTab, "wholesale"],
    ["retailers", "Retailers", RetailersTab],
    ["products", "Products", ProductsTab],
    ["event-packages", "Event Packages", EventPackagesTab],
    ["flavors", "Flavors", FlavorsTab],
    ["categories", "Flavor Categories", CategoriesTab],
    ["packs", "Party Packs", PacksTab],
    ["signups", "Guest List", SignupsTab],
    ["product-interest", "Product Interest", ProductInterestTab],
    ["faqs", "FAQs", FaqsTab],
    ["pages", "Pages", PagesTab],
    ["settings", "Settings", SettingsTab],
];

export const AdminDashboard = () => {
    const [user, setUser] = useState(null);
    const [active, setActive] = useState("bookings");
    const [badges, setBadges] = useState({});
    const navigate = useNavigate();

    useEffect(() => {
        document.title = "Backstage — Showtime Carnival Co.";
        api.get("/auth/me")
            .then((r) => setUser(r.data))
            .catch(() => navigate("/admin/login"));
    }, [navigate]);

    useEffect(() => {
        const load = () => api.get("/admin/badges").then((r) => setBadges(r.data || {})).catch(() => {});
        load();
        const t = setInterval(load, 30000);
        return () => clearInterval(t);
    }, [active]);

    const badgeFor = (key) => badges[key] || 0;

    const logout = () => {
        localStorage.removeItem("showtime_admin_token");
        navigate("/admin/login");
    };

    if (!user) return <div className="min-h-[100svh] bg-ink" />;

    const ActiveTab = TABS.find(([k]) => k === active)[2];

    return (
        <section className="min-h-[100svh] bg-paper pt-24 md:pt-28 pb-20" data-testid="admin-dashboard">
            <div className="mx-auto max-w-[1440px] px-5 md:px-10">
                <div className="flex items-center justify-between mb-8">
                    <div className="flex items-center gap-4">
                        <img src={LOGO_PINK} alt="Showtime" className="h-12 w-auto" />
                        <div>
                            <h1 className="font-oswald uppercase text-2xl">Backstage</h1>
                            <p className="text-xs text-zinc-500">{user.email}</p>
                        </div>
                    </div>
                    <button onClick={logout} data-testid="admin-logout-button" className="inline-flex items-center gap-2 rounded-full border border-zinc-300 px-5 py-2.5 text-xs font-bold uppercase tracking-widest text-zinc-600 hover:border-pink">
                        <LogOut size={14} /> Logout
                    </button>
                </div>
                <div className="md:hidden mb-6">
                    <label htmlFor="admin-tab-select" className="block text-[10px] font-bold tracking-[0.25em] uppercase text-zinc-500 mb-2">Section</label>
                    <select
                        id="admin-tab-select"
                        value={active}
                        onChange={(e) => setActive(e.target.value)}
                        data-testid="admin-tab-select"
                        className="w-full rounded-xl border border-zinc-300 bg-white px-4 py-3 text-sm font-semibold outline-none focus:ring-2 focus:ring-pink"
                    >
                        {TABS.map(([key, label, _c, badgeKey]) => (
                            <option key={key} value={key}>
                                {label}{badgeKey && badgeFor(badgeKey) > 0 ? ` (${badgeFor(badgeKey)})` : ""}
                            </option>
                        ))}
                    </select>
                </div>
                <div className="md:grid md:grid-cols-[220px_1fr] md:gap-10 md:items-start">
                    <nav className="hidden md:flex md:flex-col md:gap-1.5 md:sticky md:top-28" data-testid="admin-tabs">
                        {TABS.map(([key, label, _c, badgeKey], idx) => (
                            <Fragment key={key}>
                                {idx === 5 && <div className="my-3 border-t border-zinc-300" data-testid="admin-nav-divider" />}
                                <button
                                    onClick={() => setActive(key)}
                                    data-testid={`admin-tab-${key}`}
                                    className={`text-left rounded-xl px-4 py-2.5 font-oswald uppercase tracking-widest text-xs transition-colors flex items-center justify-between gap-2 ${
                                        active === key ? "bg-ink text-white" : "text-zinc-600 hover:bg-white hover:text-pink-dark"
                                    }`}
                                >
                                    <span>{label}</span>
                                    {badgeKey && badgeFor(badgeKey) > 0 && (
                                        <span data-testid={`admin-badge-${key}`} className="rounded-full bg-pink text-ink text-[10px] font-extrabold min-w-5 h-5 px-1.5 flex items-center justify-center">
                                            {badgeFor(badgeKey)}
                                        </span>
                                    )}
                                </button>
                            </Fragment>
                        ))}
                    </nav>
                    <div className="min-w-0">
                        <ActiveTab />
                    </div>
                </div>
            </div>
        </section>
    );
};
